import {useCallback} from 'react';

import {extractFormulaVarNames, resolveVars} from '../../contentGenerator/formulaUtils';
import {
    buildUserMessageContent,
    buildProfileRequirements,
    requestJson,
    requestReviewDescription,
    requestText,
} from '../../contentGenerator/services/ai/aiService';
import {buildDescriptionContext} from '../utils';

const resolveText = (text, vars) =>
    text && text.includes('{{') && vars?.length ? resolveVars(text, vars) : (text || '');

// ── Variable preservation across AI actions ─────────────────────────
// When the user invokes AI actions (quick tools, SEO fix, enrich,
// chat), we want the {{token}} placeholders in the source text to
// survive the round-trip — otherwise every AI rewrite bakes literal
// values into the template and breaks the whole parametric pipeline
// for subsequent variants. Two-layer defence:
//   1. Instruct the AI explicitly that tokens must be preserved, and
//      tell it what each resolves to for context only.
//   2. Post-process the AI output to restore any tokens the model
//      substituted anyway (happens occasionally with terse prompts).

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Skip restoration for very short resolved values — they cause false
// positives (e.g. "US" matching "…us…" in prose, "1" matching digits).
const MIN_RESTORABLE_LENGTH = 3;

const buildTokenPreservationBlock = (text, vars = [], contextVarNames = null) => {
    const tokenNames = extractFormulaVarNames(text || '');
    if (!tokenNames.length) return '';

    const allowedContextNames = contextVarNames?.length
        ? new Set(contextVarNames)
        : null;

    const hints = tokenNames
        .map((name) => {
            if (allowedContextNames && !allowedContextNames.has(name)) return null;
            const v = vars.find((entry) => entry.name === name);
            if (!v) return null;
            return `- {{${name}}} → "${v.sample ?? ''}"`;
        })
        .filter(Boolean);

    const tokenList = tokenNames.map((name) => `{{${name}}}`).join(', ');
    const contextBlock = hints.length
        ? `\nFor context only (do NOT substitute these values into the output):\n${hints.join('\n')}`
        : '';

    return `\n\nThe text contains placeholder tokens (${tokenList}). Keep them EXACTLY as written in your output — do not replace, resolve, paraphrase, or remove any of them. They will be filled in later at render time.${contextBlock}`;
};

const restoreTokens = (aiOutput, originalText, vars = []) => {
    if (!aiOutput || typeof aiOutput !== 'string') return aiOutput;
    const tokenNames = extractFormulaVarNames(originalText || '');
    if (!tokenNames.length) return aiOutput;

    let result = aiOutput;
    for (const name of tokenNames) {
        if (result.includes(`{{${name}}}`)) continue; // already preserved
        const v = vars.find((entry) => entry.name === name);
        const sample = typeof v?.sample === 'string' ? v.sample.trim() : '';
        if (!sample || sample.length < MIN_RESTORABLE_LENGTH) continue;

        // Case-insensitive, word-boundary-anchored, first-occurrence
        // replacement — enough to catch "Brushed Nickel" → "brushed
        // nickel" slips without false-matching substrings.
        const pattern = new RegExp(`\\b${escapeRegExp(sample)}\\b`, 'i');
        if (pattern.test(result)) {
            result = result.replace(pattern, `{{${name}}}`);
        }
    }
    return result;
};

const FIELD_TYPE_TO_PROFILE_KEY = {
    title: 'title',
    description: 'description',
    bullet: 'bullets',
};

const buildSectionRequirementBlock = ({fieldType, profileSettings}) => {
    const sectionKey = FIELD_TYPE_TO_PROFILE_KEY[fieldType];
    if (!sectionKey || !profileSettings) return '';

    return buildProfileRequirements({
        profileSettings,
        enabled: {
            title: sectionKey === 'title',
            desc: sectionKey === 'description',
            bullets: sectionKey === 'bullets',
        },
    }).trim();
};

const resolveFieldCharLimit = ({charLimit = 0, fieldType = '', profileSettings = null}) => {
    if (charLimit > 0) return charLimit;

    const sectionKey = FIELD_TYPE_TO_PROFILE_KEY[fieldType];
    return sectionKey ? profileSettings?.[sectionKey]?.maxChars || 0 : 0;
};

const buildAttachedImageInstruction = (inputImages = []) => {
    const validImageCount = inputImages.filter(
        (image) => typeof image?.url === 'string' && image.url.trim(),
    ).length;

    return validImageCount
        ? `One or more product images may be attached. Use them only to ground visible details such as finish, silhouette, installation style, and hardware form. Do not invent hidden specs or unverified features.`
        : '';
};

const buildFieldRewriteSystemPrompt = ({
    charLimit = 0,
    fieldType = '',
    profileSettings = null,
    inputImages = [],
    brandContext = '',
}) => {
    const resolvedCharLimit = resolveFieldCharLimit({
        charLimit,
        fieldType,
        profileSettings,
    });
    const requirementBlock = buildSectionRequirementBlock({fieldType, profileSettings});

    return [
        `You are an expert ecommerce content editor rewriting a single ${fieldType || 'listing'} field.`,
        'Return only the revised text with no quotes or markdown.',
        resolvedCharLimit > 0
            ? `Keep the final text within ${resolvedCharLimit} characters.`
            : '',
        buildAttachedImageInstruction(inputImages),
        brandContext,
        requirementBlock,
    ]
        .filter(Boolean)
        .join('\n\n');
};

const buildAttributesBlock = (vars = []) => {
    if (!vars.length) return '';
    return `\n\nProduct attributes (attribute name → value):\n${vars.map((v) => `- ${v.name.replace(/_/g, ' ')}: ${v.sample}`).join('\n')}\nUse these to understand what product data values mean when referenced in the content.`;
};

const buildFieldChatSystemPrompt = ({
    charLimit = 0,
    field,
    listing,
    profileSettings = null,
    inputImages = [],
    vars = [],
    brandContext = '',
}) => {
    const resolvedCharLimit = resolveFieldCharLimit({
        charLimit,
        fieldType: field.type,
        profileSettings,
    });
    const requirementBlock = buildSectionRequirementBlock({
        fieldType: field.type,
        profileSettings,
    });

    // Keep the field text with {{tokens}} intact — the preservation block
    // tells the AI what they mean and that they must stay verbatim in any
    // updatedText response. Chat replies that don't change content never
    // touch updatedText, so no restoration needed for those.
    const preservationBlock = buildTokenPreservationBlock(field.text, vars);

    return `You are an expert content editor helping refine product listing copy for ${listing.name}${listing.category ? ` (${listing.category})` : ''}.
Working on the "${field.label}" field (type: ${field.type}).
Current content: "${field.text || ''}"
${resolvedCharLimit > 0 ? `Keep updated text within ${resolvedCharLimit} characters.` : ''}
${buildAttachedImageInstruction(inputImages)}${buildAttributesBlock(vars)}${brandContext}${preservationBlock}
${requirementBlock ? `\n${requirementBlock}` : ''}

Respond ONLY with valid JSON and no markdown.
If changing content, respond with {"reply":"short explanation","updatedText":"full revised text"}.
If only answering, respond with {"reply":"short explanation"}.`;
};

export const useReviewAI = ({profileSettings = null, brandContext = ''} = {}) => {
    const generateDescription = useCallback(
        async ({apiKey, listing, charLimit, inputImages = []}) => {
            const resolvedCharLimit = resolveFieldCharLimit({
                charLimit,
                fieldType: 'description',
                profileSettings,
            });

            return requestReviewDescription({
                apiKey,
                listingContext: buildDescriptionContext(listing, resolvedCharLimit),
                charLimit: resolvedCharLimit,
                maxTokens: 600,
                inputImages,
                profileSettings,
            });
        },
        [profileSettings, brandContext],
    );

    const rewriteText = useCallback(
        async ({
            apiKey,
            charLimit,
            fieldType = 'bullet',
            prompt,
            systemPrompt,
            maxTokens = 500,
            inputImages = [],
        }) =>
            requestText({
                apiKey,
                prompt,
                systemPrompt: [
                    buildFieldRewriteSystemPrompt({
                        charLimit,
                        fieldType,
                        profileSettings,
                        inputImages,
                        brandContext,
                    }),
                    systemPrompt,
                ]
                    .filter(Boolean)
                    .join('\n\n'),
                inputImages,
                maxTokens,
            }),
        [profileSettings, brandContext],
    );

    // Preferred wrapper for any rewrite that should keep template tokens
    // intact — quick-action tools, SEO fix, enrich. Callers pass the raw
    // text (with {{tokens}}) plus vars; we bake the preservation
    // instructions into the prompt AND post-process the AI output to
    // restore any tokens the model substituted anyway.
    const rewriteTextPreservingVars = useCallback(
        async ({
            apiKey,
            charLimit,
            fieldType = 'bullet',
            originalText = '',
            instruction,
            systemPrompt,
            maxTokens = 500,
            inputImages = [],
            vars = [],
        }) => {
            const instructionTokenNames = extractFormulaVarNames(instruction || '');
            const tokenSourceText = instructionTokenNames.length
                ? `${originalText}\n${instruction}`
                : originalText;
            const preservation = buildTokenPreservationBlock(
                tokenSourceText,
                vars,
                instructionTokenNames.length ? instructionTokenNames : null,
            );
            const fullPrompt = `${instruction || ''}${preservation}\n\nText: "${originalText}"`;

            const aiOutput = await requestText({
                apiKey,
                prompt: fullPrompt,
                systemPrompt: [
                    buildFieldRewriteSystemPrompt({
                        charLimit,
                        fieldType,
                        profileSettings,
                        inputImages,
                        brandContext,
                    }),
                    systemPrompt,
                ]
                    .filter(Boolean)
                    .join('\n\n'),
                inputImages,
                maxTokens,
            });

            return restoreTokens(aiOutput, tokenSourceText, vars);
        },
        [profileSettings, brandContext],
    );

    const chatAboutField = useCallback(
        async ({apiKey, listing, field, messages, charLimit, inputImages = [], vars = []}) => {
            const raw = await requestJson({
                apiKey,
                systemPrompt: buildFieldChatSystemPrompt({
                    charLimit,
                    field,
                    listing,
                    profileSettings,
                    inputImages,
                    vars,
                    brandContext,
                }),
                messages: Array.isArray(messages)
                    ? messages.map((message, index) => {
                          const isLatestUserMessage =
                              message.role !== 'assistant' &&
                              index === messages.length - 1 &&
                              typeof message.content === 'string';

                          return isLatestUserMessage
                              ? {
                                    ...message,
                                    content: buildUserMessageContent(
                                        message.content,
                                        inputImages,
                                    ),
                                }
                              : message;
                      })
                    : messages,
                maxTokens: 700,
            });

            // If the chat response proposes updatedText, post-process it
            // to restore any {{tokens}} the model collapsed into literals.
            if (raw && typeof raw === 'object' && typeof raw.updatedText === 'string') {
                return {
                    ...raw,
                    updatedText: restoreTokens(raw.updatedText, field.text, vars),
                };
            }
            return raw;
        },
        [profileSettings, brandContext],
    );

    const adaptVariantText = useCallback(
        async ({
            apiKey,
            sourceListing,
            targetListing,
            field,
            charLimit,
            inputImages = [],
            vars = [],
        }) =>
            requestText({
                apiKey,
                systemPrompt: buildFieldRewriteSystemPrompt({
                    charLimit,
                    fieldType: field.type,
                    profileSettings,
                    inputImages,
                    brandContext,
                }),
                prompt: `Adapt product listing copy from one SKU variant to another.
Keep structure, tone, and all non-variant details identical.
Only update variant-specific references such as finish, color, or material references when the target naming suggests it.
Return only the adapted text.
${inputImages.length > 1 ? '\nAttached images are ordered as source variant first, target variant second.' : ''}

Source variant: ${sourceListing.name} (${sourceListing.sku})
Target variant: ${targetListing.name} (${targetListing.sku})
Field: ${field.label}
Text: "${resolveText(field.text, vars)}"`,
                inputImages,
                maxTokens: 500,
            }),
        [profileSettings, brandContext],
    );

    return {
        adaptVariantText,
        chatAboutField,
        generateDescription,
        rewriteText,
        rewriteTextPreservingVars,
    };
};
